import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, mergeProps as _mergeProps, vShow as _vShow, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "contentHeader flex" }
const _hoisted_2 = { class: "contentBox" }
const _hoisted_3 = { class: "block w-full" }
const _hoisted_4 = { class: "flex mb-4" }
const _hoisted_5 = ["title"]
const _hoisted_6 = ["disabled", "title"]
const _hoisted_7 = ["disabled", "title"]
const _hoisted_8 = { class: "ml-auto" }
const _hoisted_9 = { class: "text-center" }

import {onBeforeUnmount, onMounted, reactive, ref} from "vue";
import {BryntumGrid} from "@bryntum/grid-vue-3";
import {Menu, Store} from "@bryntum/grid";
import GridInputFilter from "@/components/GridInputFilter";
import {useGlobalStore} from "@/stores/GlobalStore";
import {ErrorLocationType, PopupType, TableType} from '@/utilities/enums/Enums';
import {$t} from "@/utilities/i18n/config";
import {PermissionService} from "@/utilities/services/PermissionService";
import dialog from "@/dialog";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import {GeneralResponse} from "@/models/api/Responses";
import Error from "@/components/Error";
import {SafetyInstructionInfo} from "@/models/safetyInstruction/SafetyInstructionInfo";
import {SafetyinstructionCsvExport} from "@/models/CsvExportModels";
import exportFromJSON from "export-from-json";
import {useI18n} from "vue-i18n";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage} from "@/AppConfig";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {SafetyInstructionService} from "@/utilities/services/entities/SafetyInstructionService";
import {LocationService} from "@/utilities/services/entities/LocationService";



export default /*@__PURE__*/_defineComponent({
  __name: 'SafetyInstructions',
  setup(__props) {

const gridStore = new Store();
const globalStore = useGlobalStore();
const { t } = useI18n();
const locationService = new LocationService();
const safetyInstructionService = new SafetyInstructionService();
const permissionService = new PermissionService();

let selectedSafetyInstruction = ref([]);
let selectableLocations = ref([]);

const deleteSelection = async() => {
  //drop call without selection
  if(!selectedSafetyInstruction.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t("global.delete"))
      .cancelText(t("global.cancel"))
      .confirm(t("safetyInstructions.confirmDeletionMulti", { count:selectedSafetyInstruction.value.length}));

  if (answer) {
    let someEntriesFailed = false;
    for (const si of selectedSafetyInstruction.value){
      const resp = await safetyInstructionService.deleteSafetyInstruction(si.id);

      if(resp === null || resp === undefined || resp?.statusCode !== 0){
        ErrorHandler.handleError(resp, ErrorLocationType.table);
        someEntriesFailed = true;
        continue;
      }

      gridStore.applyChangeset({removed:[{id:si.id}]});
    }

    if (someEntriesFailed) return;

    globalStore.showSuccess(t("global.deleted"));
  }
};

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};

const grid = ref(null);
const useGridConfig = {
  headerMenuFeature: false,
  relayStoreEvents: true,
  enableTextSelection: true,
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
  },
  columns : {
    data: [
      { id:'name', field: 'name', text: t('safetyInstructions.grid.name'), flex: 1, },
      { id:'text', field: 'text', text: t('safetyInstructions.grid.text'), flex: 1, },
      {
        id:'locations',
        field: 'locations',
        text: '',
        width   : 36,
				sortable: false,
        type: "template",
        template: ({ record }) => {
          if(record.data.safetyInstructionLocations.length > 0)
						return `<div class='gridRowButton'><span class='icon-pin_outline'></span></div>`;
					return

				},
      },
      {
        id:'menu',
        text	: '',
        width   : 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.safetyInstruction], event.records[0].id, event.index.toString());
        }
      },
    }
  },
  store: gridStore,
  listeners: {
    selectionChange(event) {
      if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedSafetyInstruction.value = selectedSafetyInstruction.value.concat(event.selected.map(sr => sr.data))
      selectedSafetyInstruction.value = selectedSafetyInstruction.value.filter(s => !event.deselected.map(sr => sr.data).some(sr => s?.id === sr?.id));
    },
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.originalCount;
    },
    cellClick({column, record}) {
      if (column.data.field === 'locations' && record.data.safetyInstructionLocations.length > 0) {
        globalStore.openPopup({
          data: record.data,
          action: globalStore.closeModal,
          title: t('safetyInstructions.title'),
          type: PopupType.safetyInstructionLocationsPopup
        });
      }
    },
  },
  height: currentWindowHeight,
}

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: t("global.edit"),
        cls: 'color',
        icon: 'icon-editieren',
        hidden: !permissionService.userHasPermission("SafetyInstruction.Edit"),
        weight: 100,
        onItem : ({ menu }) => {
          globalStore.openPopup({
            data:menu.record,
            action:addOrUpdateSafetyInstruction,
            resetButton:false,
            title:$t("safetyInstructions.edit"),
            type:PopupType.safetyInstructions
          });
        }
      },
      {
        text   : t("global.delete"),
        cls	: 'color',
        icon: 'icon-loeschen',
        hidden: !permissionService.userHasPermission("SafetyInstruction.Delete"),
        weight : 140,
        onItem: async ({ menu }) => {
          const answer = await dialog
              .okText(t("global.delete"))
              .cancelText(t("global.cancel"))
              .confirm(t("safetyInstructions.confirmDeletionSingle", {name: menu.record.name}));

          if (answer) {
            const resp = await safetyInstructionService.deleteSafetyInstruction(menu.record.id);

            if(resp === null || resp === undefined || resp?.statusCode !== 0){
              ErrorHandler.handleError(resp, ErrorLocationType.table);
              return;
            }

            gridStore.applyChangeset({removed:[{id:menu.record.id}]});

            globalStore.showSuccess(t('global.deleted'));
          }
        }
      }
    ],
  });

  menu.setRecord(record);

  return menu;
};
const gridConfig = reactive(useGridConfig);


const newSafetyInstruction = () => {
  const safetyInstruction = new SafetyInstructionInfo();

  globalStore.openPopup({
    data: safetyInstruction,
    action: addOrUpdateSafetyInstruction,
    resetButton: false,
    title: $t("safetyInstructions.new"),
    type: PopupType.safetyInstructions
  });
}

const addOrUpdateSafetyInstruction = async () => {
  const safetyInstruction = globalStore.modal.contentObject as SafetyInstructionInfo;

  const safetyInstructionResp = await safetyInstructionService.addOrUpdateSafetyInstruction(safetyInstruction);
  if (safetyInstructionResp === null || safetyInstructionResp === undefined || safetyInstructionResp.statusCode !== 0){
    ErrorHandler.handleError(safetyInstructionResp as GeneralResponse, ErrorLocationType.popup);
    return;
  }

  if(safetyInstruction.id < 0) safetyInstruction.id = safetyInstructionResp.id;
  // fix selection when it was an update -> replace old value in selected-array
  else if(selectedSafetyInstruction.value.some(el => el.id === safetyInstruction.id)) {
    selectedSafetyInstruction.value = selectedSafetyInstruction.value.filter(el => el.id !== safetyInstruction.id);
    selectedSafetyInstruction.value.push(safetyInstruction);
  }

  gridStore.add(safetyInstruction);
  gridStore.sort(gridStore.sorters);

  globalStore.closeModal();
  globalStore.showSuccess(t('global.saved'));
}

const exportCSV = () => {
  let data = selectedSafetyInstruction.value?.length > 0 ? selectedSafetyInstruction.value : gridStore.records;

  // remove unnecessary properties from csv
  data = data.map((item)=> new SafetyinstructionCsvExport(item.data ?? item));

  const fileName = $t('safetyInstructions.csvDownloadFilename');
  const exportType =  exportFromJSON.types.csv;

  exportFromJSON({
    data:data,
    fileName:fileName,
    exportType:exportType,
  });
};

const setGridData = async ()=>{
  // start spinner
  globalStore.gridDataIsLoading = true;

  const safetyInstructionsResp = await safetyInstructionService.getSafetyInstructions("display");

  if (safetyInstructionsResp === null || safetyInstructionsResp === undefined || safetyInstructionsResp.statusCode !== 0) {
    ErrorHandler.handleError(safetyInstructionsResp, ErrorLocationType.table);
    return;
  }

  gridStore.setStoreData(safetyInstructionsResp.safetyInstructionInfo);
  selectableLocations.value = await locationService.getLocations("selectable", ErrorLocationType.table);

  globalStore.reorderColumns(TableType[TableType.safetyInstruction]);

  globalStore.closeModal();
  // stop spinner
  globalStore.gridDataIsLoading = false;
};

onMounted(async () => {
  // calculate grid height and sub to window resize event
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(ref(gridStore));
  globalStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

  await setGridData();
});

onBeforeUnmount(()=>{
  window.removeEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(null);
  globalStore.setGridColumnStore(null);

  globalStore.currentItems = 0;
  globalStore.allItems = 0;
});

return (_ctx: any,_cache: any) => {
  const _directive_permission = _resolveDirective("permission")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h4", null, _toDisplayString(_unref($t)("safetyInstructions.title")), 1)
    ]),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("div", null, [
        _createVNode(_unref(Error), {
          errorLoc: _unref(ErrorLocationType).table
        }, null, 8, ["errorLoc"]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              onClick: newSafetyInstruction,
              title: _unref(t)('safetyInstructions.new')
            }, _cache[0] || (_cache[0] = [
              _createElementVNode("span", { class: "icon-neu" }, null, -1)
            ]), 8, _hoisted_5)), [
              [_directive_permission, 'SafetyInstruction.Create']
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedSafetyInstruction).some(()=>true),
              onClick: exportCSV,
              title: _unref(t)('safetyInstructions.csvDownload')
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("span", { class: "icon-downlaod" }, null, -1)
            ]), 8, _hoisted_6)), [
              [_directive_permission, 'SafetyInstruction.Create']
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedSafetyInstruction).some(()=>true),
              onClick: deleteSelection,
              title: _unref(t)('global.delete')
            }, _cache[2] || (_cache[2] = [
              _createElementVNode("span", { class: "icon-loeschen" }, null, -1)
            ]), 8, _hoisted_7)), [
              [_directive_permission, 'SafetyInstruction.Delete']
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(GridInputFilter), {
                gridStore: _unref(gridStore),
                hideFilterButton: true
              }, null, 8, ["gridStore"])
            ])
          ]),
          _createVNode(_unref(BryntumGrid), _mergeProps({
            ref_key: "grid",
            ref: grid
          }, gridConfig, { readOnly: true }), null, 16)
        ])
      ], 512), [
        [_vShow, !_unref(globalStore).gridDataIsLoading]
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_9, [
        _createVNode(LoadingSpinner)
      ], 512), [
        [_vShow, _unref(globalStore).gridDataIsLoading]
      ])
    ])
  ], 64))
}
}

})